import {FormField, OptionalFormField, RequiredFormField} from "../../../../validation/FormField";
import {none, option} from "ts-option";
import FormValidation from "../../../../validation/FormValidation";
import {ValidationResult} from "../../../../validation/ValidationResult";
import FormValidators from "../../../../validation/FormValidators";
import Asset, {TierLevel} from "../../../../model/asset/Asset";
import BaseFormController from "../../../../validation/BaseFormController";
import Attachment from "../../../../model/attachment/Attachment";
import AssetSpecification from "../../../../model/asset/AssetSpecification";
import AssetSpecificationBooleanSelector from "./AssetSpecificationBooleanSelector";
import NumberUtils from "../../../../utils/NumberUtils";

const uuidv4 = require("uuid/v4");

/**
 * Asset form data.
 */
export default class AssetForm extends BaseFormController<AssetForm> {
	constructor(formId: string,
		public readonly categoryId: RequiredFormField<string>,
		public readonly manufacturer: OptionalFormField<string>,
		public readonly model: RequiredFormField<string>,
		public readonly price: RequiredFormField<string>,
		public readonly secondaryPrice: RequiredFormField<string>,
		public readonly rebate: OptionalFormField<string>,
		public readonly type: OptionalFormField<string>,
		public readonly disabled: RequiredFormField<boolean>,
		public readonly attachments: OptionalFormField<Attachment[]>,
		public readonly specifications: OptionalFormField<AssetSpecification[]>,
		public readonly firstMerchantCode: OptionalFormField<string>,
		public readonly secondMerchantCode: OptionalFormField<string>,
		public readonly description: OptionalFormField<string>,
		public readonly markup: RequiredFormField<number>,
		public readonly tierLevel: RequiredFormField<TierLevel>,
		public readonly rewardsPoints: RequiredFormField<number>,
		public readonly keywords: OptionalFormField<string[]>,
		public readonly alertNote: OptionalFormField<string>,
		public readonly warrantyCode: OptionalFormField<string>,
		public readonly applianceShortCode: OptionalFormField<string>) {
		super(manufacturer, formId);
	}

	static createAssetForm({
		categoryId = none,
		manufacturer = none,
		model = none,
		price = none,
		secondaryPrice = none,
		rebate = none,
		type = none,
		disabled = none,
		attachments = none,
		specifications = none,
		firstMerchantCode = none,
		secondMerchantCode = none,
		description = none,
		markup = none,
		tierLevel = none,
		rewardsPoints = none,
		keywords = none,
		alertNote = none,
		warrantyCode = none,
		applianceShortCode = none
	}) {
		const priceField = RequiredFormField.create<string>(price, "Price is required");
		const secondaryPriceField = RequiredFormField.create<string>(secondaryPrice, "Secondary price is required");
		return new AssetForm(
			uuidv4(),
			RequiredFormField.create<string>(categoryId, "Category is required"),
			OptionalFormField.create<string>(manufacturer),
			RequiredFormField.create<string>(model, "Model is required", [
				FormValidators.nonEmptyString("Model is required")
			]),
			priceField,
			secondaryPriceField,
			OptionalFormField.create<string>(rebate, [
				FormValidators.notMoreThanCurrency('Rebate cannot be bigger than price', priceField.id)
			]),
			OptionalFormField.create<string>(type),
			RequiredFormField.create<boolean>(disabled, "Asset status is required"),
			OptionalFormField.create<Attachment[]>(attachments),
			OptionalFormField.create<AssetSpecification[]>(specifications, [this.validateAssetSpecifications]),
			OptionalFormField.create<string>(firstMerchantCode),
			OptionalFormField.create<string>(secondMerchantCode),
			OptionalFormField.create<string>(description),
			RequiredFormField.create<number>(markup, "Markup is required"),
			RequiredFormField.create<TierLevel>(tierLevel, "Tier level is required"),
			RequiredFormField.create<number>(rewardsPoints, "Rewards points are required",
				[FormValidators.numberPositive("Rewards points must be positive number")],),
			OptionalFormField.create<string[]>(keywords),
			OptionalFormField.create<string>(alertNote),
			OptionalFormField.create<string>(warrantyCode),
			OptionalFormField.create<string>(applianceShortCode)
		)
	}

	public static createEmpty(): AssetForm {
		return this.createAssetForm({disabled: option(false)});
	}

	static createFromAsset(asset: Asset) {
		return AssetForm.createAssetForm({
			categoryId: option(asset.categoryId),
			manufacturer: asset.manufacturer,
			model: option(asset.model),
			price: option((asset.price / 100).toFixed(2)),
			secondaryPrice: option((asset.secondaryPrice / 100).toFixed(2)),
			rebate: asset.rebate.map(r => (r / 100).toFixed(2)),
			type: asset.type,
			disabled: option(asset.disabled),
			attachments: option(asset.attachments),
			specifications: option(asset.specifications),
			firstMerchantCode: asset.firstMerchantCode,
			secondMerchantCode: asset.secondMerchantCode,
			description: asset.description,
			markup: option(asset.markup),
			tierLevel: option(asset.tierLevel),
			rewardsPoints: option(asset.rewardsPoints),
			keywords: option(asset.keywords),
			alertNote: asset.alertNote,
			warrantyCode: asset.warrantyCode,
			applianceShortCode: asset.applianceShortCode
		});
	}

	validate(): AssetForm {
		return FormValidation.validateForm<AssetForm>(this, (form, config) => form.copy(config));
	}

	static validateAssetSpecifications(form: BaseFormController<AssetForm>, value: AssetSpecification[]): string[] {
		const errors: string[] = [];
		if (value) {
			value.forEach((assetSpec) => {
				if (assetSpec.type === "solar-compatible" &&
					(typeof AssetSpecificationBooleanSelector.getCurrentValue(assetSpec.value) !== "boolean")) {
					errors.push(`Value for field "${assetSpec.label}" have to be "Yes" or "No"`)
				}
				if (!assetSpec.value) {
					errors.push(`Value for field "${assetSpec.type}" can't be empty`)
				}
			});
		}
		return errors
	}

	onValid(onValid: (asset: Asset) => void,
		onInvalid: () => void): ValidationResult<Asset> {
		return FormValidation.validateFields(
			[this.categoryId, this.manufacturer, this.model, this.price, this.secondaryPrice, this.rebate, this.type,
				this.disabled, this.specifications, this.markup, this.tierLevel, this.rewardsPoints],
			() => {
				return new Asset(uuidv4,
					this.categoryId.getValue(),
					this.manufacturer.getOptionalValue(),
					this.model.getValue(),
					NumberUtils.multiplyAndRound(Number(this.price.getValue()), 100),
					NumberUtils.multiplyAndRound(Number(this.secondaryPrice.getValue()), 100),
					this.rebate.getOptionalValue().map(r => NumberUtils.multiplyAndRound(Number(r), 100)),
					this.type.getOptionalValue(),
					this.disabled.getValue(),
					this.attachments.getOptionalValue().getOrElse([]),
					this.specifications.getValue().getOrElse([]),
					this.firstMerchantCode.getOptionalValue(),
					this.secondMerchantCode.getOptionalValue(),
					this.description.getOptionalValue(),
					this.markup.getValue(),
					this.tierLevel.getValue(),
					this.rewardsPoints.getValue(),
					this.keywords.getOptionalValue().getOrElse([]),
					this.alertNote.getOptionalValue(),
					this.warrantyCode.getOptionalValue(),
					this.applianceShortCode.getOptionalValue()
				);
			}).onSuccess(asset => onValid(asset)).onFailure(() => onInvalid());
	}

	copy({
		categoryId = this.categoryId,
		manufacturer = this.manufacturer,
		model = this.model,
		price = this.price,
		secondaryPrice = this.secondaryPrice,
		rebate = this.rebate,
		type = this.type,
		disabled = this.disabled,
		attachments = this.attachments,
		specifications = this.specifications,
		firstMerchantCode = this.firstMerchantCode,
		secondMerchantCode = this.secondMerchantCode,
		description = this.description,
		markup = this.markup,
		tierLevel = this.tierLevel,
		rewardsPoints = this.rewardsPoints,
		keywords = this.keywords,
		alertNote = this.alertNote,
		warrantyCode = this.warrantyCode,
		applianceShortCode = this.applianceShortCode
	}): AssetForm {
		return new AssetForm(this.formId, categoryId, manufacturer, model, price, secondaryPrice, rebate, type, disabled,
			attachments, specifications, firstMerchantCode, secondMerchantCode, description, markup, tierLevel, rewardsPoints,
			keywords, alertNote, warrantyCode, applianceShortCode);
	}

	fields(): Array<FormField<any>> {
		return [];
	}
}
